/**
 * ProfessionalExcelFormatter — Enterprise-grade Excel styling engine
 *
 * Orange/Yellow theme with professional borders, section headers,
 * conditional status colouring, and auto-fit column widths.
 *
 * Uses xlsx-js-style (style-capable fork of SheetJS) for full
 * cell styling support: fills, fonts, borders, alignment, merges.
 */

import * as XLSX from 'xlsx-js-style';

// ─── Theme Palette ───────────────────────────────────────────────────────────

const THEME = {
  // Column header row — yellow
  headerBg: 'FFD700',
  headerFg: '000000',
  // Brand section headers — orange
  brandSectionBg: 'FFA500',
  brandSectionFg: '000000',
  // Data row borders — dark border
  borderColor: '000000',
  // Status colours
  statusComplete: '059669',
  statusCompleteBg: 'D1FAE5',
  statusPartial: 'D97706',
  statusPartialBg: 'FEF3C7',
  statusMissing: 'DC2626',
  statusMissingBg: 'FEE2E2',
  statusExcess: '7C3AED',
  statusExcessBg: 'EDE9FE',
  // Overview
  overviewLabelBg: 'F1F5F9',
  overviewLabelFg: '334155',
  overviewValueFg: '0F172A',
} as const;

const THIN_BORDER = { style: 'thin' as const, color: { rgb: THEME.borderColor } };
const ALL_BORDERS = { top: THIN_BORDER, bottom: THIN_BORDER, left: THIN_BORDER, right: THIN_BORDER };

/** Helper: create a solid fill object (xlsx-js-style requires patternType) */
function solidFill(rgb: string) {
  return { patternType: 'solid' as const, fgColor: { rgb } };
}

// ─── Public API ──────────────────────────────────────────────────────────────

export class ProfessionalExcelFormatter {

  /** Style a header row (gold/yellow bg, black bold text). Defaults to row 0. */
  static applyHeaderRow(ws: XLSX.WorkSheet, colCount: number, rowIndex = 0): void {
    for (let c = 0; c < colCount; c++) {
      const addr = XLSX.utils.encode_cell({ r: rowIndex, c });
      if (!ws[addr]) ws[addr] = { v: '', t: 's' };
      ws[addr].s = {
        font: { bold: true, sz: 11, color: { rgb: THEME.headerFg } },
        fill: solidFill(THEME.headerBg),
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: ALL_BORDERS,
      };
    }
  }

  /** Style a brand section header row — bold black text on orange background, centered + merged */
  static applyBrandSectionRow(ws: XLSX.WorkSheet, rowIndex: number, colCount: number): void {
    for (let c = 0; c < colCount; c++) {
      const addr = XLSX.utils.encode_cell({ r: rowIndex, c });
      if (!ws[addr]) ws[addr] = { v: '', t: 's' };
      ws[addr].s = {
        font: { bold: true, sz: 12, color: { rgb: THEME.brandSectionFg } },
        fill: solidFill(THEME.brandSectionBg),
        alignment: { horizontal: 'center', vertical: 'center' },
        border: ALL_BORDERS,
      };
    }
    // Merge brand header across all columns
    if (colCount > 1) {
      if (!ws['!merges']) ws['!merges'] = [];
      ws['!merges'].push({
        s: { r: rowIndex, c: 0 },
        e: { r: rowIndex, c: colCount - 1 },
      });
    }
  }

  /**
   * Apply styled borders + alignment to menu data cells.
   * 'Items' column is left-aligned; all others are center-aligned.
   * White background with solid black borders on every cell.
   */
  static applyMenuDataRows(
    ws: XLSX.WorkSheet,
    dataRowIndices: number[],
    colCount: number,
    itemsColIndex: number,
  ): void {
    for (const r of dataRowIndices) {
      for (let c = 0; c < colCount; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (!ws[addr]) ws[addr] = { v: '', t: 's' };
        ws[addr].s = {
          font: { sz: 11, color: { rgb: '000000' } },
          fill: solidFill('FFFFFF'),
          alignment: {
            horizontal: c === itemsColIndex ? 'left' : 'center',
            vertical: 'center',
          },
          border: ALL_BORDERS,
        };
      }
    }
  }

  /** Apply borders + light yellow fill to all data cells (skip row 0 header) */
  static applyDataBorders(ws: XLSX.WorkSheet): void {
    const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
    for (let r = 1; r <= range.e.r; r++) {
      for (let c = range.s.c; c <= range.e.c; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (ws[addr] && !ws[addr].s?.fill) {
          ws[addr].s = {
            ...ws[addr].s,
            fill: solidFill('FFFDE7'),
            border: ALL_BORDERS,
            alignment: { vertical: 'center', ...(ws[addr].s?.alignment ?? {}) },
          };
        } else if (ws[addr]) {
          ws[addr].s = {
            ...ws[addr].s,
            border: ALL_BORDERS,
            alignment: { vertical: 'center', ...(ws[addr].s?.alignment ?? {}) },
          };
        }
      }
    }
  }

  /** Colour the Status/Readiness column cells with conditional formatting */
  static applyStatusColouring(ws: XLSX.WorkSheet, colIndex: number): void {
    const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
    for (let r = 1; r <= range.e.r; r++) {
      const addr = XLSX.utils.encode_cell({ r, c: colIndex });
      if (!ws[addr]) continue;
      const val = String(ws[addr].v ?? '').toUpperCase();
      let fg = THEME.overviewValueFg as string;
      let bg = '' as string;
      if (val === 'COMPLETE' || val === 'READY' || val === 'APPROVED') { fg = THEME.statusComplete; bg = THEME.statusCompleteBg; }
      else if (val === 'PARTIAL' || val === 'IN_REVIEW' || val === 'RESUBMITTED') { fg = THEME.statusPartial; bg = THEME.statusPartialBg; }
      else if (val === 'MISSING' || val === 'REJECTED') { fg = THEME.statusMissing; bg = THEME.statusMissingBg; }
      else if (val === 'EXCESS' || val === 'UNLOCKED FOR FIX') { fg = THEME.statusExcess; bg = THEME.statusExcessBg; }
      if (bg) {
        ws[addr].s = {
          font: { bold: true, sz: 10, color: { rgb: fg } },
          fill: solidFill(bg),
          alignment: { horizontal: 'center', vertical: 'center' },
          border: ALL_BORDERS,
        };
      }
    }
  }

  /** Style an overview (key-value) sheet: bold labels, thicker value column */
  static applyOverviewStyle(ws: XLSX.WorkSheet): void {
    const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
    // Header row
    this.applyHeaderRow(ws, 2);
    // Data rows
    for (let r = 1; r <= range.e.r; r++) {
      const labelAddr = XLSX.utils.encode_cell({ r, c: 0 });
      if (ws[labelAddr]) {
        ws[labelAddr].s = {
          font: { bold: true, sz: 10, color: { rgb: THEME.overviewLabelFg } },
          fill: solidFill(THEME.overviewLabelBg),
          alignment: { vertical: 'center' },
          border: ALL_BORDERS,
        };
      }
      const valAddr = XLSX.utils.encode_cell({ r, c: 1 });
      if (ws[valAddr]) {
        ws[valAddr].s = {
          font: { sz: 10, color: { rgb: THEME.overviewValueFg } },
          alignment: { vertical: 'center', wrapText: true },
          border: ALL_BORDERS,
        };
      }
    }
  }

  /** Auto-fit column widths by measuring all cell content lengths */
  static autoFitColumns(ws: XLSX.WorkSheet): XLSX.ColInfo[] {
    const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
    const colWidths: number[] = [];
    for (let c = range.s.c; c <= range.e.c; c++) {
      let max = 8;
      for (let r = range.s.r; r <= range.e.r; r++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (ws[addr]) {
          const len = String(ws[addr].v ?? '').length;
          if (len > max) max = len;
        }
      }
      colWidths.push(Math.min(max + 4, 55));
    }
    return colWidths.map(wch => ({ wch }));
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Visual-First Excel Engine — Dashboard headers, card tables, heatmaps
  // ═══════════════════════════════════════════════════════════════════════════

  /** Dark dashboard palette for the visual sheets */
  private static readonly VIZ = {
    dashBg: '1B1B2F',        // deep navy
    dashFg: 'FFFFFF',
    dashAccent: '3B82F6',    // cobalt blue
    dashSubFg: 'A1A1AA',     // zinc-400
    kpiCardBg: '27274A',     // card bg
    kpiValueFg: 'FBBF24',   // amber accent
    kpiLabelFg: 'A1A1AA',
    cardBorder: '3F3F5C',    // subtle border
    // Heatmap gradient (8 stops: cool→hot)
    heatGradient: ['E8EAF6', 'C5CAE9', '9FA8DA', '7986CB', '5C6BC0', '3F51B5', '303F9F', '1A237E'],
    heatFg:       ['1A237E', '1A237E', '1A237E', 'FFFFFF', 'FFFFFF', 'FFFFFF', 'FFFFFF', 'FFFFFF'],
    // Performance arrow colors
    upArrow: '10B981',       // emerald
    downArrow: 'EF4444',     // red
    neutralArrow: '71717A',
    // Data bar
    barFill: '3B82F6',
    barTrack: '2D2D4A',
    // Spend pie text colors (cycle)
    pieColors: ['3B82F6', '8B5CF6', 'F59E0B', '10B981', 'EF4444', 'EC4899', '06B6D4', 'F97316'],
  } as const;

  private static readonly THICK_BORDER = { style: 'medium' as const, color: { rgb: '3F3F5C' } };
  private static readonly CARD_BORDERS = {
    top: { style: 'medium' as const, color: { rgb: '3F3F5C' } },
    bottom: { style: 'medium' as const, color: { rgb: '3F3F5C' } },
    left: { style: 'medium' as const, color: { rgb: '3F3F5C' } },
    right: { style: 'medium' as const, color: { rgb: '3F3F5C' } },
  };

  /**
   * Write a visual dashboard header block at the top of a sheet.
   * Takes up `rows` rows (default 8). Returns the next row index for data.
   *
   * Layout:
   *  Row 0: Title (merged across colCount)
   *  Row 1: Subtitle / date
   *  Row 2: blank spacer
   *  Row 3-6: KPI cards (up to 6 side-by-side in pairs of 2 cols each)
   *  Row 7: blank spacer
   */
  static applyDashboardHeader(
    ws: XLSX.WorkSheet,
    colCount: number,
    title: string,
    subtitle: string,
    kpis: Array<{ label: string; value: string; sub?: string }>,
  ): number {
    const V = this.VIZ;

    // Fill background for header area (rows 0-7)
    for (let r = 0; r < 8; r++) {
      for (let c = 0; c < colCount; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (!ws[addr]) ws[addr] = { v: '', t: 's' };
        ws[addr].s = { fill: solidFill(V.dashBg) };
      }
    }

    // Row 0 — Title (merged)
    const t0 = XLSX.utils.encode_cell({ r: 0, c: 0 });
    ws[t0] = { v: title, t: 's' };
    ws[t0].s = {
      font: { bold: true, sz: 18, color: { rgb: V.dashFg } },
      fill: solidFill(V.dashBg),
      alignment: { horizontal: 'left', vertical: 'center' },
    };
    if (!ws['!merges']) ws['!merges'] = [];
    ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } });

    // Row 1 — Subtitle
    const t1 = XLSX.utils.encode_cell({ r: 1, c: 0 });
    ws[t1] = { v: subtitle, t: 's' };
    ws[t1].s = {
      font: { sz: 10, color: { rgb: V.dashSubFg } },
      fill: solidFill(V.dashBg),
      alignment: { horizontal: 'left', vertical: 'center' },
    };
    ws['!merges'].push({ s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } });

    // Row 2 — spacer (already filled with dashBg)

    // Rows 3-4: KPI cards (each occupies 2 columns: label col + value col)
    const kpiStartRow = 3;
    const maxPerRow = Math.min(Math.floor(colCount / 2), 4);
    kpis.forEach((kpi, i) => {
      const row = kpiStartRow + Math.floor(i / maxPerRow) * 2;
      const col = (i % maxPerRow) * 2;
      if (col + 1 >= colCount) return;

      // Value cell (top of card)
      const vAddr = XLSX.utils.encode_cell({ r: row, c: col });
      ws[vAddr] = { v: kpi.value, t: 's' };
      ws[vAddr].s = {
        font: { bold: true, sz: 16, color: { rgb: V.kpiValueFg } },
        fill: solidFill(V.kpiCardBg),
        alignment: { horizontal: 'center', vertical: 'center' },
        border: { top: this.THICK_BORDER, left: this.THICK_BORDER, right: this.THICK_BORDER },
      };
      // Merge value across 2 cols
      ws['!merges'].push({ s: { r: row, c: col }, e: { r: row, c: col + 1 } });

      // Label cell (below value)
      const lAddr = XLSX.utils.encode_cell({ r: row + 1, c: col });
      ws[lAddr] = { v: `${kpi.label}${kpi.sub ? ` · ${kpi.sub}` : ''}`, t: 's' };
      ws[lAddr].s = {
        font: { sz: 8, color: { rgb: V.kpiLabelFg } },
        fill: solidFill(V.kpiCardBg),
        alignment: { horizontal: 'center', vertical: 'top' },
        border: { bottom: this.THICK_BORDER, left: this.THICK_BORDER, right: this.THICK_BORDER },
      };
      ws['!merges'].push({ s: { r: row + 1, c: col }, e: { r: row + 1, c: col + 1 } });

      // Fill right side of merge to keep bg consistent
      const vAddr2 = XLSX.utils.encode_cell({ r: row, c: col + 1 });
      if (!ws[vAddr2]) ws[vAddr2] = { v: '', t: 's' };
      ws[vAddr2].s = { fill: solidFill(V.kpiCardBg), border: { top: this.THICK_BORDER, right: this.THICK_BORDER } };
      const lAddr2 = XLSX.utils.encode_cell({ r: row + 1, c: col + 1 });
      if (!ws[lAddr2]) ws[lAddr2] = { v: '', t: 's' };
      ws[lAddr2].s = { fill: solidFill(V.kpiCardBg), border: { bottom: this.THICK_BORDER, right: this.THICK_BORDER } };
    });

    // Rows in gap between KPI and data
    const kpiRows = Math.ceil(kpis.length / maxPerRow) * 2;
    const dataStart = kpiStartRow + kpiRows + 1;

    // Set row heights for header area
    if (!ws['!rows']) ws['!rows'] = [];
    ws['!rows'][0] = { hpt: 32 };
    ws['!rows'][1] = { hpt: 18 };
    ws['!rows'][2] = { hpt: 10 };
    for (let r = kpiStartRow; r < kpiStartRow + kpiRows; r += 2) {
      ws['!rows'][r] = { hpt: 28 };
      ws['!rows'][r + 1] = { hpt: 16 };
    }

    return dataStart;
  }

  /**
   * Style a data grid as "card table" — thick outer borders, subtle inner borders,
   * alternating row shading, and elevated visual weight.
   * @param dataStartRow - first row of data (after header row)
   * @param headerRowIdx - the header row index
   */
  static applyCardTable(ws: XLSX.WorkSheet, colCount: number, headerRowIdx: number, dataStartRow: number): void {
    const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
    const V = this.VIZ;

    // Header row — dark style
    for (let c = 0; c < colCount; c++) {
      const addr = XLSX.utils.encode_cell({ r: headerRowIdx, c });
      if (!ws[addr]) ws[addr] = { v: '', t: 's' };
      ws[addr].s = {
        font: { bold: true, sz: 10, color: { rgb: V.dashFg } },
        fill: solidFill('1E1E3A'),
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: this.CARD_BORDERS,
      };
    }

    // Data rows with alternating bands + thick card borders
    for (let r = dataStartRow; r <= range.e.r; r++) {
      const isEven = (r - dataStartRow) % 2 === 0;
      for (let c = 0; c < colCount; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (!ws[addr]) ws[addr] = { v: '', t: 's' };
        const existing = ws[addr].s ?? {};
        ws[addr].s = {
          ...existing,
          fill: existing.fill ?? solidFill(isEven ? 'F8FAFC' : 'EFF6FF'),
          font: existing.font ?? { sz: 10, color: { rgb: '1E293B' } },
          alignment: { vertical: 'center', ...(existing.alignment ?? {}) },
          border: {
            top: { style: 'thin' as const, color: { rgb: 'CBD5E1' } },
            bottom: { style: 'thin' as const, color: { rgb: 'CBD5E1' } },
            left: c === 0 ? this.THICK_BORDER : { style: 'thin' as const, color: { rgb: 'E2E8F0' } },
            right: c === colCount - 1 ? this.THICK_BORDER : { style: 'thin' as const, color: { rgb: 'E2E8F0' } },
          },
        };
      }
    }

    // Bottom thick border on last row
    if (range.e.r >= dataStartRow) {
      for (let c = 0; c < colCount; c++) {
        const addr = XLSX.utils.encode_cell({ r: range.e.r, c });
        if (ws[addr]?.s) {
          ws[addr].s.border = { ...ws[addr].s.border, bottom: this.THICK_BORDER };
        }
      }
    }
  }

  /**
   * Write a "Spend Distribution" pseudo-pie using colored text blocks.
   * Since xlsx doesn't embed real charts, we use a formatted legend table.
   * Writes starting at (startRow, 0). Returns next row.
   */
  static applySpendPieLegend(
    ws: XLSX.WorkSheet,
    startRow: number,
    segments: Array<{ label: string; value: number; pct: string }>,
    colCount: number,
  ): number {
    const V = this.VIZ;
    // Section title
    const titleAddr = XLSX.utils.encode_cell({ r: startRow, c: 0 });
    ws[titleAddr] = { v: '◉  SPEND DISTRIBUTION', t: 's' };
    ws[titleAddr].s = {
      font: { bold: true, sz: 12, color: { rgb: V.dashAccent } },
      fill: solidFill(V.dashBg),
      alignment: { horizontal: 'left', vertical: 'center' },
    };
    if (!ws['!merges']) ws['!merges'] = [];
    ws['!merges'].push({ s: { r: startRow, c: 0 }, e: { r: startRow, c: Math.min(colCount - 1, 3) } });
    // Fill rest of title row
    for (let c = 1; c < colCount; c++) {
      const a = XLSX.utils.encode_cell({ r: startRow, c });
      if (!ws[a]) ws[a] = { v: '', t: 's' };
      ws[a].s = { fill: solidFill(V.dashBg) };
    }

    let r = startRow + 1;
    segments.forEach((seg, i) => {
      const clr = V.pieColors[i % V.pieColors.length];
      const block = `■`;
      // Color block + label
      const a0 = XLSX.utils.encode_cell({ r, c: 0 });
      ws[a0] = { v: `${block}  ${seg.label}`, t: 's' };
      ws[a0].s = {
        font: { bold: true, sz: 10, color: { rgb: clr } },
        fill: solidFill(V.dashBg),
        alignment: { horizontal: 'left', vertical: 'center' },
      };
      // Value
      const a1 = XLSX.utils.encode_cell({ r, c: 1 });
      ws[a1] = { v: `QAR ${seg.value.toLocaleString()}`, t: 's' };
      ws[a1].s = {
        font: { sz: 10, color: { rgb: V.dashFg } },
        fill: solidFill(V.dashBg),
        alignment: { horizontal: 'right', vertical: 'center' },
      };
      // Percentage
      const a2 = XLSX.utils.encode_cell({ r, c: 2 });
      ws[a2] = { v: seg.pct, t: 's' };
      ws[a2].s = {
        font: { bold: true, sz: 10, color: { rgb: clr } },
        fill: solidFill(V.dashBg),
        alignment: { horizontal: 'center', vertical: 'center' },
      };
      // Fill remaining cols
      for (let c = 3; c < colCount; c++) {
        const ax = XLSX.utils.encode_cell({ r, c });
        if (!ws[ax]) ws[ax] = { v: '', t: 's' };
        ws[ax].s = { fill: solidFill(V.dashBg) };
      }
      r++;
    });
    // Spacer row
    for (let c = 0; c < colCount; c++) {
      const a = XLSX.utils.encode_cell({ r, c });
      if (!ws[a]) ws[a] = { v: '', t: 's' };
      ws[a].s = { fill: solidFill(V.dashBg) };
    }
    return r + 1;
  }

  /**
   * Write a "Top N" pseudo-bar chart using Unicode block characters.
   * Each bar is a single row: Label | ████████░░░  | Value
   */
  static applyBarChart(
    ws: XLSX.WorkSheet,
    startRow: number,
    title: string,
    bars: Array<{ label: string; value: number }>,
    colCount: number,
  ): number {
    const V = this.VIZ;
    const maxVal = Math.max(...bars.map(b => b.value), 1);

    // Title
    const tAddr = XLSX.utils.encode_cell({ r: startRow, c: 0 });
    ws[tAddr] = { v: `◉  ${title}`, t: 's' };
    ws[tAddr].s = {
      font: { bold: true, sz: 12, color: { rgb: V.dashAccent } },
      fill: solidFill(V.dashBg),
      alignment: { horizontal: 'left', vertical: 'center' },
    };
    if (!ws['!merges']) ws['!merges'] = [];
    ws['!merges'].push({ s: { r: startRow, c: 0 }, e: { r: startRow, c: Math.min(colCount - 1, 3) } });
    for (let c = 1; c < colCount; c++) {
      const a = XLSX.utils.encode_cell({ r: startRow, c });
      if (!ws[a]) ws[a] = { v: '', t: 's' };
      ws[a].s = { fill: solidFill(V.dashBg) };
    }

    let r = startRow + 1;
    bars.forEach((bar, i) => {
      const filledLen = Math.round((bar.value / maxVal) * 20);
      const barStr = '█'.repeat(filledLen) + '░'.repeat(20 - filledLen);
      const clr = V.pieColors[i % V.pieColors.length];

      // Label
      const a0 = XLSX.utils.encode_cell({ r, c: 0 });
      ws[a0] = { v: bar.label, t: 's' };
      ws[a0].s = {
        font: { sz: 10, color: { rgb: V.dashFg } },
        fill: solidFill(V.dashBg),
        alignment: { horizontal: 'left', vertical: 'center' },
      };
      // Bar
      const a1 = XLSX.utils.encode_cell({ r, c: 1 });
      ws[a1] = { v: barStr, t: 's' };
      ws[a1].s = {
        font: { sz: 10, name: 'Consolas', color: { rgb: clr } },
        fill: solidFill(V.dashBg),
        alignment: { horizontal: 'left', vertical: 'center' },
      };
      if (colCount > 3) {
        ws['!merges'].push({ s: { r, c: 1 }, e: { r, c: colCount - 2 } });
      }
      // Value
      const aLast = XLSX.utils.encode_cell({ r, c: colCount - 1 });
      ws[aLast] = { v: bar.value, t: 'n' };
      ws[aLast].s = {
        font: { bold: true, sz: 10, color: { rgb: V.kpiValueFg } },
        fill: solidFill(V.dashBg),
        alignment: { horizontal: 'right', vertical: 'center' },
      };
      // Fill middle cells
      for (let c = 2; c < colCount - 1; c++) {
        const ax = XLSX.utils.encode_cell({ r, c });
        if (!ws[ax]) ws[ax] = { v: '', t: 's' };
        ws[ax].s = { fill: solidFill(V.dashBg) };
      }
      r++;
    });
    // Spacer
    for (let c = 0; c < colCount; c++) {
      const a = XLSX.utils.encode_cell({ r, c });
      if (!ws[a]) ws[a] = { v: '', t: 's' };
      ws[a].s = { fill: solidFill(V.dashBg) };
    }
    return r + 1;
  }

  // ─── Deep Dive Heatmap & Visual Enhancements ──────────────────────────

  /**
   * Apply heatmap gradient colours to a numeric column.
   * Cells shift from light indigo (low) → deep indigo (high).
   */
  static applyHeatmapColumn(ws: XLSX.WorkSheet, colIndex: number, startRow: number): void {
    const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
    const V = this.VIZ;

    // Collect all numeric values in column
    const vals: number[] = [];
    for (let r = startRow; r <= range.e.r; r++) {
      const addr = XLSX.utils.encode_cell({ r, c: colIndex });
      if (ws[addr] && typeof ws[addr].v === 'number') vals.push(ws[addr].v as number);
    }
    if (vals.length === 0) return;
    const minV = Math.min(...vals);
    const maxV = Math.max(...vals);
    const spread = maxV - minV || 1;

    for (let r = startRow; r <= range.e.r; r++) {
      const addr = XLSX.utils.encode_cell({ r, c: colIndex });
      if (!ws[addr] || typeof ws[addr].v !== 'number') continue;
      const normalized = (ws[addr].v as number - minV) / spread; // 0–1
      const gradIdx = Math.min(Math.floor(normalized * V.heatGradient.length), V.heatGradient.length - 1);
      ws[addr].s = {
        ...ws[addr].s,
        fill: solidFill(V.heatGradient[gradIdx]),
        font: { ...(ws[addr].s?.font ?? {}), bold: true, color: { rgb: V.heatFg[gradIdx] } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: this.CARD_BORDERS,
      };
    }
  }

  /**
   * Prepend performance arrows (▲/▼/►) to numeric cells based on a threshold.
   * Values above threshold get ▲ (green), below get ▼ (red), equal get ► (grey).
   */
  static applyPerformanceArrows(ws: XLSX.WorkSheet, colIndex: number, startRow: number, threshold: number): void {
    const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
    const V = this.VIZ;

    for (let r = startRow; r <= range.e.r; r++) {
      const addr = XLSX.utils.encode_cell({ r, c: colIndex });
      if (!ws[addr]) continue;
      const raw = ws[addr].v;
      // Parse numeric value (handle percentage strings too)
      let num: number;
      if (typeof raw === 'number') { num = raw; }
      else if (typeof raw === 'string') {
        const parsed = parseFloat(raw.replace(/[^0-9.\-]/g, ''));
        if (isNaN(parsed)) continue;
        num = parsed;
      } else continue;

      const arrow = num > threshold ? '▲' : num < threshold ? '▼' : '►';
      const arrowClr = num > threshold ? V.upArrow : num < threshold ? V.downArrow : V.neutralArrow;

      ws[addr].v = `${arrow} ${raw}`;
      ws[addr].t = 's';
      ws[addr].s = {
        ...ws[addr].s,
        font: { bold: true, sz: 10, color: { rgb: arrowClr } },
        alignment: { horizontal: 'center', vertical: 'center' },
      };
    }
  }

  /**
   * Add inline data bars using Unicode block characters in an adjacent column.
   * Inserts bar representations showing progress toward a target value.
   */
  static applyInlineDataBars(
    ws: XLSX.WorkSheet,
    valueColIndex: number,
    barColIndex: number,
    startRow: number,
    targetValue: number,
  ): void {
    const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
    const V = this.VIZ;

    for (let r = startRow; r <= range.e.r; r++) {
      const valAddr = XLSX.utils.encode_cell({ r, c: valueColIndex });
      const barAddr = XLSX.utils.encode_cell({ r, c: barColIndex });
      if (!ws[valAddr]) continue;

      let num: number;
      if (typeof ws[valAddr].v === 'number') num = ws[valAddr].v as number;
      else {
        const p = parseFloat(String(ws[valAddr].v ?? '').replace(/[^0-9.\-]/g, ''));
        if (isNaN(p)) continue;
        num = p;
      }

      const pct = Math.min(num / (targetValue || 1), 1);
      const filled = Math.round(pct * 15);
      const bar = '█'.repeat(filled) + '░'.repeat(15 - filled);
      const pctLabel = ` ${Math.round(pct * 100)}%`;

      if (!ws[barAddr]) ws[barAddr] = { v: '', t: 's' };
      ws[barAddr].v = bar + pctLabel;
      ws[barAddr].t = 's';
      ws[barAddr].s = {
        font: { sz: 9, name: 'Consolas', color: { rgb: pct >= 0.8 ? V.upArrow : pct >= 0.5 ? V.kpiValueFg : V.downArrow } },
        fill: solidFill(V.barTrack),
        alignment: { horizontal: 'left', vertical: 'center' },
        border: this.CARD_BORDERS,
      };
    }
  }

  /**
   * Apply a "sparkline" trend using Unicode mini-bars in a cell.
   * Takes an array of values and creates a tiny visual: ▁▃▅▇▅▃▁
   */
  static sparkline(values: number[]): string {
    if (values.length === 0) return '';
    const blocks = ['▁', '▂', '▃', '▄', '▅', '▆', '▇', '█'];
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    return values.map(v => {
      const idx = Math.min(Math.floor(((v - min) / range) * blocks.length), blocks.length - 1);
      return blocks[idx];
    }).join('');
  }
}
